import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../services/notification.service';
import {
  Notification,
  NotificationStatus,
  NotificationPriority,
} from '../../models/notification.model';
import { Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  faCheck,
  faArchive,
  faClock,
  faFilter,
  faSearch,
  faInfoCircle,
  faCalendarCheck,
  faFileSignature,
  faCalendarAlt,
  faTimes,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationReminderModalComponent } from '../notification-reminder-modal/notification-reminder-modal.component';
import { NotificationAction } from '../../models/notification.model';
import { AppToastService } from '../../../toast/app-toast.service';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';

@Component({
  selector: 'san-notification-overview',
  templateUrl: './notification-overview.component.html',
  styleUrls: ['./notification-overview.component.scss'],
  standalone: false,
})
export class NotificationOverviewComponent implements OnInit {
  faCheck = faCheck;
  faArchive = faArchive;
  faClock = faClock;
  faFilter = faFilter;
  faCalendarAlt = faCalendarAlt;
  faSearch = faSearch;
  faTimes = faTimes;
  faFileAlt = faFileAlt;

  // Map for action icons
  actionIcons: Record<string, any> = {
    faCheck: faCheck,
    faTimes: faTimes,
    faFileAlt: faFileAlt,
  };

  // Icons for types
  icons: Record<string, IconDefinition> = {
    SYSTEM: faInfoCircle,
    APPOINTMENT: faCalendarCheck,
    MESSAGE: faFileSignature,
    S: faInfoCircle,
    A: faCalendarCheck,
    M: faFileSignature,
    DEFAULT: faInfoCircle,
  };

  allFilteredNotifications$: Observable<Notification[]>;
  pagedNotifications$: Observable<Notification[]>;
  totalCount$: Observable<number>;

  statusFilter$ = new BehaviorSubject<string>('ALL');
  typeFilter$ = new BehaviorSubject<string>('ALL');
  searchTerm$ = new BehaviorSubject<string>('');
  showArchived$ = new BehaviorSubject<boolean>(false);
  currentPage$ = new BehaviorSubject<number>(1);
  pageSize = 10;

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private modalService: NgbModal,
    private toastService: AppToastService,
  ) {
    this.allFilteredNotifications$ = combineLatest([
      this.notificationService.notifications$,
      this.statusFilter$,
      this.typeFilter$,
      this.searchTerm$,
      this.showArchived$,
    ]).pipe(
      map(([notifications, status, type, search, showArchived]) => {
        return notifications
          .filter((n) => {
            const matchesStatus =
              status === 'ALL'
                ? n.status !== NotificationStatus.ARCHIVED || showArchived
                : (n.status as string) === status;
            const matchesType =
              type === 'ALL' ||
              n.type === type ||
              n.messageType === type ||
              n.message_type === type;
            const matchesSearch =
              (n.title || '')
                .toLowerCase()
                .includes((search || '').toLowerCase()) ||
              (n.message || '')
                .toLowerCase()
                .includes((search || '').toLowerCase());
            return matchesStatus && matchesType && matchesSearch;
          })
          .sort((a, b) => {
            const dateA = a.createdAt ? a.createdAt.getTime() : 0;
            const dateB = b.createdAt ? b.createdAt.getTime() : 0;
            return dateB - dateA;
          });
      }),
    );

    this.totalCount$ = this.allFilteredNotifications$.pipe(
      map((n) => n.length),
    );

    this.pagedNotifications$ = combineLatest([
      this.allFilteredNotifications$,
      this.currentPage$,
    ]).pipe(
      map(([notifications, page]) => {
        const startIndex = (page - 1) * this.pageSize;
        return notifications.slice(startIndex, startIndex + this.pageSize);
      }),
    );

    // Reset to page 1 when filters change
    combineLatest([
      this.statusFilter$,
      this.typeFilter$,
      this.searchTerm$,
      this.showArchived$,
    ]).subscribe(() => {
      this.currentPage$.next(1);
    });
  }

  ngOnInit(): void {
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.notificationService.loadNotifications({ limit: 100 }).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  markAsRead(id: string): void {
    this.notificationService.markAsRead(id).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  viewDetails(id: string): void {
    this.router.navigate(['/notifications', id]);
  }

  archive(id: string): void {
    this.notificationService.archive(id).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  setReminder(id: string): void {
    const notification = this.notificationService.getById(id);
    if (!notification) return;

    const modalRef = this.modalService.open(
      NotificationReminderModalComponent,
      { centered: true },
    );
    (
      modalRef.componentInstance as NotificationReminderModalComponent
    ).notificationTitle = notification.title;

    modalRef.result.then(
      (result) => {
        if (result instanceof Date) {
          this.notificationService.setReminder(id, result).subscribe({
            next: () => {},
            error: () => {},
          });
        }
      },
      () => {
        // dismissed
      },
    );
  }

  handleAction(
    notification: Notification,
    action: NotificationAction,
    event: Event,
  ): void {
    event.stopPropagation();
    if (action.internalRoute) {
      this.toastService.show(
        'Aktion ausgeführt',
        `Aktion "${action.label}" wurde für "${notification.title}" aufgerufen.`,
        'success',
      );
      // In einer echten App würde man hier navigieren oder eine API aufrufen
      // this.router.navigate([action.internalRoute]);
    }
  }

  resetFilters(): void {
    this.statusFilter$.next('ALL');
    this.typeFilter$.next('ALL');
    this.searchTerm$.next('');
    this.showArchived$.next(false);
    // We also need to reset the select elements in the HTML,
    // but for a click-dummy this logical reset is a good start.
    // In a production app, we would use Reactive Forms.
  }

  getIconForType(type: string) {
    return this.icons[type] || this.icons['DEFAULT'];
  }

  getStatusClass(status: NotificationStatus): string {
    switch (status) {
      case NotificationStatus.UNREAD:
        return 'bg-primary';
      case NotificationStatus.READ:
        return 'bg-secondary';
      case NotificationStatus.ARCHIVED:
        return 'bg-dark';
      default:
        return 'bg-light';
    }
  }

  getPriorityClass(priority: NotificationPriority): string {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'text-danger';
      case NotificationPriority.HIGH:
        return 'text-warning';
      case NotificationPriority.MEDIUM:
        return 'text-info';
      case NotificationPriority.LOW:
        return 'text-muted';
      default:
        return '';
    }
  }
}
